import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import AdminTable from 'app/components/admin/AdminTable';
import { Button, Flex, FlexColumn, Heading, Popover, Spinner, Tag, Text } from 'core/components';
import { RemoveButton } from 'core/form/components';
import { CELL_TYPES, NewButton } from 'core/components/table';
import { dateTimeFormatter } from 'core/util/dateUtils';
import storeLoader from 'app/stores/storeLoader';
import { MCP_AUTH_TYPE } from '@kentik/ui-shared/kaizen/constants';
import McpServerDialog from './McpServerDialog';
import OAuthAccessCell from './OAuthAccessCell';

const McpConnectionCell = observer(({ $mcpServers, id }) => {
  const status = $mcpServers.statusMap.get(id);
  if (!status || status.loading) {
    return <Spinner size={12} />;
  }
  if (status.connected === null) {
    return <Tag minimal>Unknown</Tag>;
  }
  if (!status.connected) {
    return (
      <Tag minimal intent="danger">
        Unreachable
      </Tag>
    );
  }
  if (status.authenticated === false) {
    return (
      <Tag minimal intent="warning">
        Not authenticated
      </Tag>
    );
  }
  return (
    <Tag minimal intent="success">
      Connected
    </Tag>
  );
});

@storeLoader('$mcpServers', '$credentials.groupedCollection', '$users')
@inject('$auth')
@observer
class McpServersTab extends Component {
  state = {
    isAddDialogOpen: false,
    isEditDialogOpen: false,
    selectedModel: undefined
  };

  componentDidUpdate(prevProps) {
    const { loading, $mcpServers } = this.props;

    if (prevProps.loading && !loading) {
      $mcpServers.checkAllStatuses({ force: true });
      $mcpServers.fetchAllOAuthStatuses();
    }
  }

  get columns() {
    const { $auth, $mcpServers, $users } = this.props;
    const canUpdate = $auth.hasRbacPermissions(['ai.mcp_servers::update']);
    const canDelete = $auth.hasRbacPermissions(['ai.mcp_servers::delete']);

    return [
      {
        id: 'name',
        label: 'Name',
        name: 'name',
        flexBasis: 20
      },
      {
        id: 'url',
        label: 'URL',
        name: 'url',
        flexBasis: 20
      },
      {
        id: 'enabled_tools',
        label: 'Tools',
        name: 'enabled_tools',
        flexBasis: 10,
        renderer: ({ value, model }) => {
          const discovered = model.get('discovered_tools');
          if (!discovered || !Array.isArray(discovered) || discovered.length === 0) {
            return <Text muted>Not discovered</Text>;
          }
          if (!value || !Array.isArray(value) || value.length === 0) {
            return <Text muted>None enabled</Text>;
          }
          return `${value.length} of ${discovered.length} enabled`;
        }
      },
      {
        id: 'connection',
        label: 'Connection',
        name: 'id',
        flexBasis: 10,
        renderer: ({ value: id }) => <McpConnectionCell $mcpServers={$mcpServers} id={id} />
      },
      {
        id: 'enabled',
        label: 'Status',
        name: 'enabled',
        flexBasis: 8,
        renderer: ({ value }) => (
          <Tag minimal intent={value ? 'success' : 'none'}>
            {value ? 'Enabled' : 'Disabled'}
          </Tag>
        )
      },
      {
        id: 'auth_type',
        label: 'Auth',
        name: 'auth_type',
        flexBasis: 10,
        renderer: ({ value, model }) => {
          if (value === MCP_AUTH_TYPE.OAUTH) {
            return (
              <Tag minimal intent="primary">
                OAuth
              </Tag>
            );
          }

          return <Tag minimal>{model.get('credential_name') ? 'Headers' : 'None'}</Tag>;
        }
      },
      {
        id: 'your_access',
        label: 'Your Access',
        name: 'id',
        flexBasis: 12,
        renderer: ({ model }) => <OAuthAccessCell $mcpServers={$mcpServers} model={model} />
      },
      {
        id: 'created_by',
        label: 'Added By',
        name: 'created_by',
        flexBasis: 12,
        hidden: true,
        renderer: ({ value }) => {
          const user = value && $users.collection.get(value);
          return user ? user.get('user_full_name') : '--';
        }
      },
      {
        id: 'created_at',
        label: 'Created',
        name: 'created_at',
        flexBasis: 12,
        hidden: true,
        renderer: dateTimeFormatter
      },
      {
        id: 'updated_at',
        label: 'Modified',
        name: 'updated_at',
        flexBasis: 12,
        hidden: true,
        renderer: dateTimeFormatter
      },
      {
        id: 'type',
        label: 'Type',
        name: 'type',
        flexBasis: 8,
        hidden: true,
        renderer: ({ value }) => (
          <Tag minimal intent={value === 'private' ? 'warning' : 'primary'}>
            {value === 'private' ? 'Private' : 'Public'}
          </Tag>
        )
      },
      {
        id: 'disabled_at',
        label: 'Last Disabled',
        name: 'disabled_at',
        flexBasis: 12,
        hidden: true,
        renderer: ({ value }) => (value ? dateTimeFormatter({ value }) : '--')
      },
      {
        type: CELL_TYPES.ACTION,
        actions: [
          (model) => {
            const tools = model.get('discovered_tools') || [];
            if (tools.length === 0) {
              return null;
            }
            return (
              <Popover
                key="tools"
                position="bottom-left"
                content={
                  <FlexColumn p={2} gap="4px" maxWidth={320} maxHeight={240} overflow="auto">
                    <Text fontWeight="bold" mb="4px">
                      Functions ({tools.length})
                    </Text>
                    {tools.map((t) => (
                      <FlexColumn key={t.name}>
                        <Text fontWeight={600} fontSize={13}>
                          {t.name}
                        </Text>
                        {t.description && (
                          <Text small muted style={{ wordBreak: 'break-word' }}>
                            {t.description}
                          </Text>
                        )}
                      </FlexColumn>
                    ))}
                  </FlexColumn>
                }
              >
                <Button icon="code" minimal small title="View Functions" />
              </Popover>
            );
          },
          canUpdate
            ? (model) => <Button key="edit" icon="edit" onClick={() => this.handleEdit(model)} minimal small />
            : null,
          canDelete
            ? (model) => (
                <RemoveButton
                  key="remove"
                  model={model}
                  showIcon
                  hideText
                  entityName="MCP server"
                  small
                  onRemove={() => model.destroy()}
                />
              )
            : null
        ].filter(Boolean)
      }
    ];
  }

  handleAdd = () => {
    const { $mcpServers } = this.props;
    this.setState({ isAddDialogOpen: true, selectedModel: $mcpServers.collection.forge() });
  };

  handleEdit = (model) => {
    this.setState({ isEditDialogOpen: true, selectedModel: model });
  };

  handleCloseDialog = () => {
    const { $mcpServers } = this.props;
    const { isAddDialogOpen, isEditDialogOpen } = this.state;
    const hadDialog = isAddDialogOpen || isEditDialogOpen;
    this.setState({ isAddDialogOpen: false, isEditDialogOpen: false, selectedModel: undefined }, () => {
      if (hadDialog) {
        $mcpServers.checkAllStatuses({ force: true });
        $mcpServers.fetchAllOAuthStatuses();
      }
    });
  };

  get emptyState() {
    const { $auth } = this.props;
    return (
      <FlexColumn alignItems="center" justifyContent="center" height="100%" p={2}>
        <FlexColumn maxWidth="60%" alignItems="flex-start">
          <Heading level={3}>Connect external MCP servers to AI Advisor</Heading>
          <Text muted>
            MCP (Model Context Protocol) servers extend AI Advisor with tools from external systems like Jira, Netbox,
            ServiceNow, and more. Each server becomes a tool that AI Advisor can work with.
          </Text>
          {$auth.hasRbacPermissions(['ai.mcp_servers::create']) && (
            <NewButton mt={2} fill={false} text="Add MCP Server" onClick={this.handleAdd} showIcon={false} />
          )}
        </FlexColumn>
      </FlexColumn>
    );
  }

  render() {
    const { isAddDialogOpen, isEditDialogOpen, selectedModel } = this.state;
    const { $mcpServers, $auth, loading } = this.props;

    if (loading) {
      return (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <Spinner size={40} />
        </Flex>
      );
    }

    if ($mcpServers.collection.size === 0) {
      return (
        <>
          {this.emptyState}
          {isAddDialogOpen && selectedModel && (
            <McpServerDialog isOpen model={selectedModel} onClose={this.handleCloseDialog} />
          )}
        </>
      );
    }

    return (
      <FlexColumn height="100%" p={2} flex={1}>
        <Flex justifyContent="flex-end" mb={2}>
          {$auth.hasRbacPermissions(['ai.mcp_servers::create']) && (
            <NewButton ml={2} entityName="MCP Server" showIcon onClick={this.handleAdd} intent="none" />
          )}
        </Flex>
        <AdminTable
          collection={$mcpServers.collection}
          columns={this.columns}
          fetchCollectionOnMount={false}
          searchBar={false}
          showColumnSelector
          defaultColumns={['name', 'url', 'enabled_tools', 'connection', 'auth_type', 'your_access', 'enabled']}
          tabPrefId="mcpServersTable"
          flexed
        />
        {(isAddDialogOpen || isEditDialogOpen) && selectedModel && (
          <McpServerDialog isOpen model={selectedModel} onClose={this.handleCloseDialog} />
        )}
      </FlexColumn>
    );
  }
}

export default McpServersTab;
